import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Tooltip,
  Skeleton,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  format,
  subDays,
  parseISO,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subMonths,
} from "date-fns";

import {
  USER_ENGAGEMENT_ENDPOINT,
  USER_ENGAGEMENT_EXPORT_ENDPOINT,
  DAILY_USAGE_ANALYTICS_ENDPOINT,
  GET_TENANT_DETAILS,
} from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import typography from "../../cssUtils/typography";
import CustomTabs from "../../common/custom-tabs";
import ButtonTwo from "../../common/dashboard-button";
import AppDatePicker from "../../common/app-date-picker";
import { loadValue, saveValue, FILTER_NAMESPACES } from "../../utils/filterStorage";

const BotbridgeAnalytics = ({ refreshKey }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { getValidToken } = useAuth();
  const [billingCurrency, setBillingCurrency] = useState("INR");

  // State management
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState(() => {
    const saved = loadValue(FILTER_NAMESPACES.BOTBRIDGE_ANALYTICS);
    return (saved && saved.selectedFilter) || "MONTH-TO-DATE";
  });
  const [selectedBifurcationFilter, setSelectedBifurcationFilter] = useState(() => {
    const saved = loadValue(FILTER_NAMESPACES.BOTBRIDGE_ANALYTICS);
    return (saved && saved.selectedBifurcationFilter) || "marketing";
  });
  const [chartData, setChartData] = useState([]);

  const [summaryData, setSummaryData] = useState({
    messagesEnqueued: 0,
    messagesSent: 0,
    messagesDelivered: 0,
    messagesRead: 0,
    messagesFailed: 0,
  });

  const [metrics, setMetrics] = useState({
    enqueuedToSentRate: 0,
    deliveryRate: 0,
    readRate: 0,
    failureRate: 0,
  });

  const [usageData, setUsageData] = useState({
    marketing: 0,
    utility: 0,
    service: 0,
    authentication: 0,
    international: 0,
    totalCharges: 0,
  });

  // FIXED: Separate state for failure data
  const [failureData, setFailureData] = useState({
    ecosystemFailures: 0,
    undeliverableFailures: 0,
    experimentFailures: 0,
    otherFailures: 0,
  });

  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setDate(today.getDate() - 365);

  const calculateMetricsFrom = (data) => ({
    enqueuedToSentRate:
      data.messagesEnqueued > 0
        ? ((data.messagesSent / data.messagesEnqueued) * 100).toFixed(1)
        : 0,
    deliveryRate:
      data.messagesSent > 0
        ? ((data.messagesDelivered / data.messagesSent) * 100).toFixed(1)
        : 0,
    readRate:
      data.messagesDelivered > 0
        ? ((data.messagesRead / data.messagesDelivered) * 100).toFixed(1)
        : 0,
    failureRate:
      data.messagesEnqueued > 0
        ? ((data.messagesFailed / data.messagesEnqueued) * 100).toFixed(1)
        : 0,
  });

  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode?.toUpperCase()) {
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      case "INR":
        return "₹";
      case "AED":
        return "د.إ";
      default:
        return "₹"; // Default to Indian Rupee symbol
    }
  };

  const [dateRange, setDateRange] = useState(() => {
    const saved = loadValue(FILTER_NAMESPACES.BOTBRIDGE_ANALYTICS);
    if (saved && saved.dateRange) {
      return {
        startDate: new Date(saved.dateRange.startDate),
        endDate: new Date(saved.dateRange.endDate),
        hasError: false,
      };
    }
    return { startDate: startOfMonth(today), endDate: today, hasError: false };
  });

  const [exportMessage, setExportMessage] = useState("");
  const [exportMessageSeverity, setExportMessageSeverity] = useState("success");

  const filterOptions = [
    "MONTH-TO-DATE",
    "LAST MONTH",
    "QUARTER-TO-DATE",
    "YEAR-TO-DATE",
  ];

  const filterOptionsForBifurcationTabs = ["marketing", "utility", "service"];

  useEffect(() => {
    saveValue(FILTER_NAMESPACES.BOTBRIDGE_ANALYTICS, {
      selectedFilter,
      selectedBifurcationFilter,
      ...(!dateRange.hasError && {
        dateRange: {
          startDate: dateRange.startDate.toISOString(),
          endDate: dateRange.endDate.toISOString(),
        },
      }),
    });
  }, [selectedFilter, selectedBifurcationFilter, dateRange]);

  // Fetch data when component mounts or date range changes
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([
        fetchEngagementData(selectedBifurcationFilter),
        fetchUsageData(selectedBifurcationFilter),
        fetchTenantDetails(),
      ]);
      setLoading(false);
    };
    loadAllData();
  }, [dateRange, refreshKey, selectedBifurcationFilter]);

  useEffect(() => {
    if (exportMessage) {
      const timer = setTimeout(() => setExportMessage(""), 4000);
      return () => clearTimeout(timer);
    }
  }, [exportMessage]);

  const getPercent = (count, total) => {
    const safeCount = Number(count) || 0;
    const safeTotal = Number(total) || 0;
    return safeTotal > 0 ? ((safeCount / safeTotal) * 100).toFixed(1) : 0;
  };

  const applyPredefinedFilter = (filter) => {
    const today = new Date();
    let startDate, endDate;

    switch (filter) {
      case "LAST MONTH":
        const lastMonth = subMonths(today, 1);
        startDate = startOfMonth(lastMonth);
        endDate = endOfMonth(lastMonth);
        break;
      case "MONTH-TO-DATE":
        startDate = startOfMonth(today);
        endDate = today;
        break;
      case "QUARTER-TO-DATE":
        startDate = startOfQuarter(today);
        endDate = today;
        break;
      case "YEAR-TO-DATE":
        startDate = startOfYear(today);
        endDate = today;
        break;
      default:
        startDate = startOfMonth(today);
        endDate = today;
    }

    setDateRange({
      startDate,
      endDate,
      hasError: false,
    });
  };

  const fetchUsageData = async (selectedBifurcationFilter) => {
    if (dateRange.hasError || dateRange.startDate > dateRange.endDate) {
      return;
    }

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const startDateStr = format(dateRange.startDate, "yyyy-MM-dd");
      const endDateStr = format(dateRange.endDate, "yyyy-MM-dd");

      // Correct (country-rate-aware) endpoint — accepts an arbitrary from/to
      // range in one call, so quarter-to-date/year-to-date spans don't need
      // stitching across multiple month-scoped requests.
      const queryParams = new URLSearchParams({
        from_date: startDateStr,
        to_date: endDateStr,
      });

      const response = await fetch(
        `${DAILY_USAGE_ANALYTICS_ENDPOINT}?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch usage data");
      }

      const jsonData = await response.json();
      console.log("GET Usage Data: ", jsonData);

      // Process usage data for message types and charges
      const summary = (Array.isArray(jsonData.usage) ? jsonData.usage : []).reduce(
        (acc, usage) => {
          acc.marketing += usage.marketing || 0;
          acc.utility += usage.utility || 0;
          acc.service += usage.services || 0;
          acc.authentication += usage.authentication || 0;
          acc.totalCharges += parseFloat(usage.total || 0);
          return acc;
        },
        {
          marketing: 0,
          utility: 0,
          service: 0,
          authentication: 0,
          international: 0,
          totalCharges: 0,
        },
      );

      setUsageData(summary);
    } catch (error) {
      console.error("Error fetching usage data:", error.message);
    }
  };

  const fetchTenantDetails = async () => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(GET_TENANT_DETAILS, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-ID": tenant,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Tenant Details:", data);

      // Set billing currency from tenant details
      if (
        data.results &&
        data.results.length > 0 &&
        data.results[0].billing_currency
      ) {
        setBillingCurrency(data.results[0].billing_currency);
      }
    } catch (error) {
      console.error("Error fetching tenant details:", error);
    }
  };

  const fetchEngagementData = async (selectedBifurcationFilter) => {
    if (dateRange.hasError || dateRange.startDate > dateRange.endDate) {
      return;
    }

    setLoading(true);

    // Format dates for API query
    const startDateStr = format(dateRange.startDate, "yyyy-MM-dd");
    const endDateStr = format(dateRange.endDate, "yyyy-MM-dd");

    const queryParams = new URLSearchParams({
      startDate: startDateStr,
      endDate: endDateStr,
      category: selectedBifurcationFilter,
    });

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(
        `${USER_ENGAGEMENT_ENDPOINT}?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      const jsonData = await response.json();
      console.log("GET engagement response: ", jsonData);

      processEngagementData(jsonData);
    } catch (error) {
      console.error("Error fetching data:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const processEngagementData = (data) => {
    // If no data from API, use sample data
    if (!data || !data.dailyStats) {
      return;
    }

    // FIXED: Process chart data correctly
    // const formattedChartData = data.dailyStats.map(day => ({
    //   date: format(new Date(`${day.date} ${new Date().getFullYear()}`), 'MMM d'),
    //   enqueued: day.messagesEnqueued,
    //   sent: day.messagesSent,
    //   delivered: day.messagesDelivered,
    //   read: day.messagesRead,
    //   failed: day.messagesFailed || 0
    // }));

    // const y = dateRange.endDate.getFullYear();

    // Build daily records with a Date object
    const dailyRecords = data.dailyStats.map((d) => {
      // --- 🟢 FIX: correct year assignment across year boundary ---
      // Start by trying startDate's year
      let parsed = new Date(`${d.date} ${dateRange.startDate.getFullYear()}`);
      // If this parsed date is before the selected startDate, push it to next year
      if (parsed < dateRange.startDate) {
        parsed.setFullYear(parsed.getFullYear() + 1);
      }

      return {
        dateObj: parsed,
        date: format(parsed, "MMM d"),
        enqueued: d.messagesEnqueued,
        sent: d.messagesSent,
        delivered: d.messagesDelivered,
        read: d.messagesRead,
        failed: d.messagesFailed || 0,
      };
    });

    const diffInDays = Math.round(
      (dateRange.endDate - dateRange.startDate) / (1000 * 60 * 60 * 24),
    );

    // For QTD/YTD -> aggregate by month
    let chart;
    if (
      ["QUARTER-TO-DATE", "YEAR-TO-DATE"].includes(selectedFilter) ||
      (selectedFilter === "CUSTOM" && diffInDays > 90)
    ) {
      const byMonth = dailyRecords.reduce((acc, rec) => {
        const key = format(rec.dateObj, "yyyy-MM");
        if (!acc[key]) {
          acc[key] = {
            // Label: 'Jan', 'Feb', ...
            date: format(rec.dateObj, "MMM yyyy"),
            enqueued: 0,
            sent: 0,
            delivered: 0,
            read: 0,
            failed: 0,
          };
        }
        acc[key].enqueued += rec.enqueued;
        acc[key].sent += rec.sent;
        acc[key].delivered += rec.delivered;
        acc[key].read += rec.read;
        acc[key].failed += rec.failed;
        return acc;
      }, {});
      chart = Object.keys(byMonth)
        .sort()
        .map((k) => byMonth[k]);
    } else {
      // keep daily for MTD / Last Month / custom short ranges
      chart = dailyRecords;
    }

    const hasNonZeroValues = chart.some(
      (d) =>
        d.enqueued > 0 ||
        d.sent > 0 ||
        d.delivered > 0 ||
        d.read > 0 ||
        d.failed > 0,
    );
    setChartData(hasNonZeroValues ? chart : []);

    // FIXED: Use actual summary data from API
    const summaryFromAPI = {
      messagesEnqueued: data.summary.messagesEnqueued ?? 0,
      messagesSent: data.summary.messagesSent ?? 0,
      messagesDelivered: data.summary.messagesDelivered ?? 0,
      messagesRead: data.summary.messagesRead ?? 0,
      messagesFailed: data.summary.messagesFailed ?? 0,
    };

    // FIXED: Properly extract failure data from API response
    const failuresFromAPI = {
      ecosystemFailures: data.summary.failures?.ecosystem ?? 0,
      undeliverableFailures: data.summary.failures?.undeliverable ?? 0,
      experimentFailures: data.summary.failures?.experiment ?? 0,
      otherFailures:
        (data.summary.messagesFailed ?? 0) -
        ((data.summary.failures?.ecosystem ?? 0) +
          (data.summary.failures?.undeliverable ?? 0) +
          (data.summary.failures?.experiment ?? 0)),
    };

    // // Check if all values are zero
    // const hasNonZeroValues = formattedChartData.some(day =>
    //   day.enqueued > 0 || day.sent > 0 || day.delivered > 0 || day.read > 0 || day.failed > 0
    // );

    // if (!hasNonZeroValues) {
    //   setChartData([]);
    // } else {
    //   setChartData(formattedChartData);
    // }

    const totals = {
      // Enqueued = Enqueued + Sent + Delivered + Read + Failed
      messagesEnqueued:
        (data.summary?.messagesEnqueued ?? 0) +
        (data.summary?.messagesSent ?? 0) +
        (data.summary?.messagesDelivered ?? 0) +
        (data.summary?.messagesRead ?? 0) +
        (data.summary?.messagesFailed ?? 0),

      // Sent = Sent + Delivered + Read + Failed
      messagesSent:
        (data.summary?.messagesSent ?? 0) +
        (data.summary?.messagesDelivered ?? 0) +
        (data.summary?.messagesRead ?? 0) +
        (data.summary?.messagesFailed ?? 0),

      // Delivered = Delivered + Read + Failed
      messagesDelivered:
        (data.summary?.messagesDelivered ?? 0) +
        (data.summary?.messagesRead ?? 0) +
        (data.summary?.messagesFailed ?? 0),

      // Read = Read
      messagesRead: data.summary?.messagesRead ?? 0,

      // Failed = Failed
      messagesFailed: data.summary?.messagesFailed ?? 0,
    };

    // FIXED: Set states with actual API data
    setSummaryData(summaryFromAPI);
    setFailureData(failuresFromAPI); // FIXED: Set failure data separately
    setMetrics(calculateMetricsFrom(summaryFromAPI));
  };

  const { enqueuedToSentRate, deliveryRate, readRate, failureRate } = metrics;
  console.log("Current metrics:", metrics);
  console.log("Current failure data:", failureData);

  const mainMetrics = [
    {
      title: "Messages Enqueued",
      value: summaryData.messagesEnqueued.toLocaleString(),
      subtitle: null,
      infoTooltip:
        "The number of messages your business initiated from BotBridge.",
    },
    {
      title: "Messages Sent",
      value: summaryData.messagesSent.toLocaleString(),
      subtitle: `${enqueuedToSentRate}% of enqueued`,
      infoTooltip: "The number of messages your business sent to customers.",
    },
    {
      title: "Messages Delivered",
      value: summaryData.messagesDelivered.toLocaleString(),
      subtitle: `${deliveryRate}% delivery rate`,
      infoTooltip:
        "The number of messages that were delivered within 7 days of the message being sent.",
    },
    {
      title: "Messages Read",
      value: summaryData.messagesRead.toLocaleString(),
      subtitle: `${readRate}% read rate`,
      infoTooltip:
        "The number of messages your business sent to customers that were delivered and read within 7 days of the message being sent.",
    },
    {
      title: "Messages Failed",
      value: summaryData.messagesFailed.toLocaleString(),
      subtitle: `${failureRate}% failure rate`,
      infoTooltip: "The number of messages that failed to be sent.",
    },
    // {
    //   title: "Total Spent",
    //   value: `${getCurrencySymbol(billingCurrency)}${usageData.totalCharges.toLocaleString()}`,
    //   subtitle: null,
    //   infoTooltip: "The total amount of money spent during the time period across all messages.",
    // },
  ];

  const messageTypes = [
    {
      title: "Service",
      value: (usageData?.service ?? 0).toLocaleString(),
      percent: `(${getPercent(usageData?.service, summaryData.messagesSent)}%)`,
    },
    {
      title: "Utility",
      value: (usageData?.utility ?? 0).toLocaleString(),
      percent: `(${getPercent(usageData?.utility, summaryData.messagesSent)}%)`,
    },
    {
      title: "Marketing",
      value: (usageData?.marketing ?? 0).toLocaleString(),
      percent: `(${getPercent(usageData?.marketing, summaryData.messagesSent)}%)`,
    },
    {
      title: "Authentication",
      value: (usageData?.authentication ?? 0).toLocaleString(),
      percent: `(${getPercent(usageData?.authentication, summaryData.messagesSent)}%)`,
    },
    {
      title: "International",
      value: (usageData?.international ?? 0).toLocaleString(),
      percent: `(${getPercent(usageData?.international, summaryData.messagesSent)}%)`,
    },
  ];

  // FIXED: Use failureData state instead of summaryData for failures
  const failureTypes = [
    {
      title: "Ecosystem",
      value: (failureData.ecosystemFailures ?? 0).toLocaleString(),
      percent: `(${getPercent(failureData.ecosystemFailures, summaryData.messagesFailed)}%)`,
    },
    {
      title: "Undeliverable",
      value: (failureData.undeliverableFailures ?? 0).toLocaleString(),
      percent: `(${getPercent(failureData.undeliverableFailures, summaryData.messagesFailed)}%)`,
    },
    {
      title: "Experiment",
      value: (failureData.experimentFailures ?? 0).toLocaleString(),
      percent: `(${getPercent(failureData.experimentFailures, summaryData.messagesFailed)}%)`,
    },
    {
      title: "Other",
      value: (failureData.otherFailures ?? 0).toLocaleString(),
      percent: `(${getPercent(failureData.otherFailures, summaryData.messagesFailed)}%)`,
    },
  ];

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    applyPredefinedFilter(filter);
  };

  const handleBifurcationFilterChange = (filter) => {
    setSelectedBifurcationFilter(filter);
  };

  const getDateRangeError = (start, end) => {
    const today = new Date();
    if (start > today || end > today) {
      return "Dates cannot be in the future";
    }
    const diffInDays = Math.abs((end - start) / (1000 * 60 * 60 * 24));
    if (diffInDays > 365) {
      return "Date range cannot be more than 1 year";
    }
    if (end < start) {
      return "End date cannot be before start date";
    }
    return "";
  };

  const handleStartDateChange = (newDate) => {
    if (!newDate) return;

    const errorMsg = getDateRangeError(newDate, dateRange.endDate);

    setDateRange((prev) => ({
      ...prev,
      startDate: newDate,
      hasError: !!errorMsg,
      errorMsg,
    }));

    // setSelectedFilter("CUSTOM");
  };

  const handleEndDateChange = (newDate) => {
    if (!newDate) return;

    const errorMsg = getDateRangeError(dateRange.startDate, newDate);

    setDateRange((prev) => ({
      ...prev,
      endDate: newDate,
      hasError: !!errorMsg,
      errorMsg,
    }));

    // setSelectedFilter("CUSTOM");
  };

  const handleReset = () => {
    const newDateRange = {
      startDate: startOfMonth(new Date()),
      endDate: new Date(),
      hasError: false,
    };
    setDateRange(newDateRange);
    setSelectedFilter("MONTH-TO-DATE");
  };

  const handleExport = async () => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const startDateStr = format(dateRange.startDate, "yyyy-MM-dd");
      const endDateStr = format(dateRange.endDate, "yyyy-MM-dd");

      const queryParams = new URLSearchParams({
        startDate: startDateStr,
        endDate: endDateStr,
      });

      const response = await fetch(
        `${USER_ENGAGEMENT_EXPORT_ENDPOINT}?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": tenant,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to export data");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `user-engagement-${startDateStr}-to-${endDateStr}.csv`;

      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setExportMessage("Export completed successfully");
      setExportMessageSeverity("success");
    } catch (error) {
      console.error("Error exporting data:", error.message);
      setExportMessage("Failed to export data. Please try again.");
      setExportMessageSeverity("error");
    }
  };

  const dateDifferenceInDays = () => {
    if (dateRange.hasError) {
      return "Invalid date range";
    }
    return `${Math.round((dateRange.endDate - dateRange.startDate) / (1000 * 60 * 60 * 24))} days`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper elevation={3} sx={{ p: 2, backgroundColor: "white" }}>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={`item-${index}`}
              variant="body2"
              style={{ color: entry.color }}
            >
              {entry.dataKey === "enqueued"
                ? "Messages enqueued: "
                : entry.dataKey === "sent"
                  ? "Messages sent: "
                  : entry.dataKey === "delivered"
                    ? "Messages delivered: "
                    : entry.dataKey === "read"
                      ? "Messages read: "
                      : "Messages failed: "}
              {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  const MetricCard = ({
    title,
    value,
    change,
    isPositive,
    subtitle,
    infoTooltip,
  }) => (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography sx={{ ...typography.bodyLarge}}>
            {title}
          </Typography>
          {infoTooltip && (
            <Tooltip title={infoTooltip}>
              <IconButton size="small" sx={{ ...typography.bodySmall }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography sx={{ ...typography.statLarge }}>
          {value}
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          mb={subtitle ? 0.5 : 0}
        ></Box>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" display="block">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const BreakdownCard = ({ title, value, percent, change, isPositive }) => (
    <Box
      sx={{
        textAlign: "center",
        p: 2,
        borderRadius: 2,
        bgcolor: "grey.50",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        transition: "background-color 0.2s ease-in-out",
        "&:hover": {
          bgcolor: "grey.100",
        },
      }}
    >
      <Typography
        sx={{ ...typography.bodyLarge, mb: 2}}
      >
        {title}
      </Typography>
      <Typography sx={{ ...typography.statLarge}}>
        {value}
      </Typography>
      <Typography
        sx={{ ...typography.bodyLarge, mb: 2}}
      >
        {percent}
      </Typography>
    </Box>
  );

  const MetricCardSkeleton = () => (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="100%" height={32} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="60%" height={16} />
      </CardContent>
    </Card>
  );

  const ChartSkeleton = () => (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width="30%" height={24} sx={{ mb: 2 }} />
      <Box sx={{ height: 320, display: "flex", flexDirection: "column", gap: 1 }}>
        <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 1 }} />
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Skeleton variant="text" width={100} height={20} />
          <Skeleton variant="text" width={100} height={20} />
          <Skeleton variant="text" width={100} height={20} />
        </Box>
      </Box>
    </Box>
  );

  const BreakdownSkeleton = () => (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      {[1, 2, 3, 4].map((i) => (
        <Box
          key={i}
          sx={{
            flex: "1 1 calc(25% - 8px)",
            minWidth: 150,
            p: 2,
            borderRadius: 2,
          }}
        >
          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={28} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="70%" height={16} />
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        p: 2.5,
        pt: 0,
        bgcolor: "grey.50",
        minHeight: "100vh",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={isMobile ? "flex-start" : "center"}
        mb={3}
        flexDirection={isMobile ? "column" : "row"}
        gap={2}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {exportMessage && (
            <Box
              sx={{
                height: "fit-content",
                py: 0.5,
                px: 1.5,
                fontSize: "0.85rem",
                backgroundColor:
                  exportMessageSeverity === "success"
                    ? "success.main"
                    : "error.main",
                color: "white",
                borderRadius: 1,
              }}
            >
              {exportMessage}
            </Box>
          )}
          {/* <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={dateRange.hasError || loading}
            sx={{
              bgcolor: "#3b82f6",
              "&:hover": { bgcolor: "#2563eb" },
              borderRadius: 2,
              textTransform: "uppercase",
              fontWeight: 500,
              py: 1.2,
              px: 2.5,
              boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
            }}
          >
            Export
          </Button> */}
        </Box>
      </Box>

      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography sx={{ ...typography.h1, fontWeight: 500 }}>
              Filter selected by Category:
            </Typography>
            <Typography sx={{ ...typography.h1 }}>
              {selectedBifurcationFilter.toUpperCase()}
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexDirection={isMobile ? "column" : "row"}
            gap={2}
          >
            <Box display="flex" gap={1} flexWrap="wrap">
              <CustomTabs
                value={selectedBifurcationFilter}
                onChange={(e, val) => handleBifurcationFilterChange(val)}
                tabs={filterOptionsForBifurcationTabs.map((option) => ({
                  label: option.toUpperCase(),
                  value: option,
                }))}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography sx={{ ...typography.h1, fontWeight: 500 }}>
              Filter Selected:
            </Typography>
            <Typography sx={{ ...typography.h1 }}>{selectedFilter}</Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexDirection={isMobile ? "column" : "row"}
            gap={2}
            marginBottom={4}
            width="100%"
          >
            <CustomTabs
              value={selectedFilter}
              onChange={(e, val) => handleFilterChange(val)}
              tabs={filterOptions.map((option) => ({
                label: option,
                value: option,
              }))}
            />
          </Box>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexDirection={isMobile ? "column" : "row"}
            gap={2}
            marginBottom={2}
          >
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Typography sx={{ ...typography.h2}}>
                Custom Dates:
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box>
                  <AppDatePicker
                    format="dd/MM/yyyy"
                    disableFuture
                    value={dateRange.startDate}
                    onChange={handleStartDateChange}
                    minDate={oneYearAgo}
                    maxDate={today}
                    slotProps={{ textField: { sx: { width: 140 } } }}
                  />
                </Box>

                <Box>
                  <AppDatePicker
                    format="dd/MM/yyyy"
                    disableFuture
                    value={dateRange.endDate}
                    onChange={handleEndDateChange}
                    minDate={dateRange.startDate || oneYearAgo}
                    maxDate={today}
                    slotProps={{ textField: { sx: { width: 140 } } }}
                  />
                </Box>
              </LocalizationProvider>
              <ButtonTwo
                variant="outlined"
                size="small"
                onClick={handleReset}
                sx={{
                  borderRadius: 1.5,
                  textTransform: "uppercase",
                  fontWeight: 500,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Reset
              </ButtonTwo>
            </Box>
          </Box>

          <Typography sx={{ ...typography.bodyLarge, marginTop: 2}}>
            Date Range: {dateDifferenceInDays()}
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2.5} mb={4}>
        {loading
          ? Array.from({ length: 5 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                <MetricCardSkeleton />
              </Grid>
            ))
          : mainMetrics.map((metric, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                <MetricCard {...metric} />
              </Grid>
            ))}
      </Grid>

      <Card
        sx={{
          mb: 4,
          borderRadius: 3,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography sx={{ ...typography.h1, mb: 3}}>
            Message Performance Trends
          </Typography>
          <Box height={320}>
            {loading ? (
              <ChartSkeleton />
            ) : chartData.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  No Data Found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  No messages were recorded for the selected date range.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: "#6b7280" }}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#6b7280" }}
                    gridLines={{ stroke: "#f3f4f6" }}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ paddingTop: "20px" }}
                    iconType="circle"
                  />
                  <Line
                    type="monotone"
                    dataKey="enqueued"
                    stroke="#8aa725ff"
                    strokeWidth={2}
                    dot={{ fill: "#8aa725ff", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Messages Enqueued"
                  />
                  <Line
                    type="monotone"
                    dataKey="sent"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Messages Sent"
                  />
                  <Line
                    type="monotone"
                    dataKey="delivered"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: "#8b5cf6", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Messages Delivered"
                  />
                  <Line
                    type="monotone"
                    dataKey="read"
                    stroke="#06b6d4"
                    strokeWidth={2}
                    dot={{ fill: "#06b6d4", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Messages Read"
                  />
                  <Line
                    type="monotone"
                    dataKey="failed"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={{ fill: "#ef4444", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Messages Failed"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </CardContent>
      </Card>

      {selectedBifurcationFilter !== "service" &&
        (loading ? (
          <Card
            sx={{
              mb: 4,
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Skeleton variant="text" width="15%" height={28} sx={{ mb: 2 }} />
              <BreakdownSkeleton />
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={8}>
            <Grid item xs={12} lg={12}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: "100%",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    sx={{ ...typography.h1}}
                    mb={3}
                  >
                    Failures
                  </Typography>

                  <Grid container spacing={2}>
                    {failureTypes.map((failure, index) => (
                      <Grid item xs={12} sm={3} key={index}>
                        <BreakdownCard {...failure} />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ))}
    </Box>
  );
};

export default BotbridgeAnalytics;
